/**
 * "Build Your Belt-It" configurator.
 *
 * Wired up to real Shopify products. Housing and Motor are now separate,
 * standalone real products (see file footer note "Housing + Motor split"),
 * so picking "housing" then "motor" in this UI adds TWO real cart line
 * items -- one Housing-family product (shared across all its motor
 * options) and one Motor Unit product (shared across every housing that
 * uses that exact motor) -- rather than one product per housing+motor
 * combo. buildLineItems() reflects that directly.
 *
 * Flow is Housing -> Motor -> Arms -> Added to Cart. There is no separate
 * "review" step: the summary sidebar (visible on steps 1-3) and the action
 * button under the arms grid both say "Add to Cart" and add the
 * configuration straight away, jumping directly to the confirmation page.
 *
 * Selecting vs. confirming: clicking a housing or motor card no longer
 * jumps straight to the next step. It sets a "preview" selection instead,
 * which drives a preview panel above the card grid showing a product
 * photo, name, description and price for whatever's currently previewed --
 * so a shopper can click through a few options and compare them before
 * committing. Only clicking that panel's "Confirm Selection" button locks
 * the choice in and advances to the next step. Arms work slightly
 * differently since they're multi-select: clicking an arm card still
 * toggles it in/out of the basket immediately (as before), but also sets
 * it as the currently-previewed arm so its photo/description/price show in
 * the same panel, with the existing "Add to Cart" button doubling as that
 * panel's confirm action.
 *
 * Electric motor pick (BF only): "Electric 240V"/"Electric 110V" in BF's
 * motor grid are two-step options -- picking one doesn't resolve to a real
 * product on its own. It reveals a second row of brand/type cards (Green
 * 800W Fixed Speed, Bosch 900W Variable Speed, Metabo 1100W Variable
 * Speed), each a real, separately-priced Motor Unit product variant at
 * that voltage (see ELECTRIC_BRAND_OPTIONS below and resolveMotor()).
 * Confirm Selection stays disabled until a brand is chosen too. This
 * mirrors the identical choice offered on the standalone BG20/BG30 product
 * pages (assets/power-unit-picker.js) -- both pull from the same three
 * shared motor products, so a price change to one is reflected everywhere
 * it's offered. Baby Belt-It, Advanced Belt-It and Heavy Duty Belt-It keep
 * their original flat, single-brand Electric option (Baby -> Bosch,
 * Advanced/Heavy Duty -> Metabo) and resolve immediately like Pneumatic
 * does, with no brand step.
 *
 * Product images: sections/beltit-configurator.liquid passes a variantId ->
 * image URL lookup via data-product-images, built from the real Shopify
 * products behind every housing and motor option. None of the relevant
 * products have images uploaded yet, so until a merchant adds some, every
 * preview panel shows a plain "Photo coming soon" placeholder instead of a
 * broken image -- this is expected, not a bug.
 *
 * The preview panel (photo, name, description, price and the confirm
 * action) sits at the TOP of each step, directly under the intro copy and
 * above the card grid -- not below it -- so it's the first thing visible
 * once a shopper clicks an option, without needing to scroll past the grid.
 *
 * Cart icon bubble / drawer sync: addConfigurationToCart() requests the
 * cart-icon-bubble (and cart-drawer, if the theme is using drawer mode)
 * sections back from /cart/add.js and patches them into the DOM directly,
 * via updateCartUI(). Without this the header cart count only updated on
 * the next full page load, since a plain /cart/add.js call with no
 * `sections` param never touches the DOM outside the configurator itself.
 * updateCartUI() deliberately does NOT open the cart drawer, even though
 * assets/cart-drawer.js's own renderContents() normally does -- the
 * configurator already has its own "Added to your basket" confirmation
 * step, so popping the drawer open on top of that would be a jarring
 * double confirmation.
 *
 * Cart-add error handling: Shopify's /cart/add.js can return a normal,
 * parseable JSON (or even HTML, if a storefront-level rate-limit/bot check
 * intercepts the request) body on FAILURE too -- e.g. a sold-out variant,
 * or an "additional verification required" challenge page. A request that
 * merely didn't throw is not the same as one that actually added something
 * to the cart, so addConfigurationToCart() explicitly checks res.ok and
 * the Shopify error-response shape (`data.status` is present on failures)
 * before resolving, and onClick's catch handler surfaces the real message
 * instead of silently showing the "Added to your basket" confirmation
 * screen regardless of what actually happened.
 *
 * Housing + Motor split (see also assets/power-unit-picker.js, the
 * standalone BG10/BG20/BG30 equivalent of this same idea): each housing
 * family (Beltafile, Baby Belt-It, Advanced Belt-It, Heavy Duty Belt-It)
 * is now its own real "<Family> Housing" product with a single variant,
 * priced as the bare housing. Motors are grouped into shared "Motor Unit"
 * products by brand -- Pneumatic Motor (0.3/0.5/0.8hp variants), Green
 * Fixed Speed Motor (240V/110V), Bosch Variable Speed 900W Motor
 * (240V/110V), Metabo Variable Speed 1100W Motor (240V/110V) -- reused
 * across every housing that takes that exact motor, per
 * SECO Beltit Configure Tables.xlsx. Each machine below carries its own
 * housingVariantId/housingPrice, and each motor option carries the real
 * shared Motor Unit product's variantId/price (BF's Electric options are
 * the exception -- see above); buildLineItems() adds both housing and
 * motor as separate real line items instead of one combined product.
 *
 * Arms: the arm products were rebuilt from scratch from
 * SECO Beltit Configure Tables.xlsx ("Arms" sheet), replacing the earlier
 * legacy-migration "Beltit Arms - Group A/B/C/D" products (now archived).
 * The spreadsheet only splits arms by "Housing Compatibility" into three
 * families -- BF (Beltafile, 15 arms), BFM (Mini Beltafile, 4 arms,
 * excluded -- BFM itself isn't in this configurator), and BI (21 arms,
 * shared identically by Baby, Advanced, and Heavy Duty housings; the
 * spreadsheet doesn't give those three any different arms from one
 * another). So:
 *   - "Beltafile Arms" product = the 15 BF-coded arms.
 *   - "Baby BELT-IT Arms", "Advanced BELT-IT Arms", "Heavy Duty BELT-IT
 *     Arms" products each = the *same* 21 BI-coded arms, duplicated across
 *     three separate Shopify products (one per housing) at the user's
 *     request, so each housing can be managed/priced independently even
 *     though the specs are identical today.
 * Every arm now has a real price from the spreadsheet (no more POA arms)
 * and a real description + belt length, so all four housings use the
 * length-grouped arm-picker layout (see groupArmsByLength()) -- 457mm /
 * 533mm / 610mm groups, shortest to longest.
 *
 * Excluded from this configurator (per explicit instruction): BG10/BG20/
 * BG30 (these get their own standalone product pages -- see
 * assets/power-unit-picker.js -- since they have no arms/multi-step flow),
 * and Mini Beltafile (BFM) -- BFM bundles arm choice into the housing
 * variant itself rather than offering a separate arms product, so it
 * didn't fit this flow.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // Data model
  // ---------------------------------------------------------------------

  // The 21 BI-coded arms are identical across Baby/Advanced/Heavy Duty (see
  // file header) -- built once here and reused per housing with each
  // housing's own real variantIds spliced in, so the three arrays don't
  // have to be maintained by hand in three places.
  var BI_ARM_SPECS = [
    { sku: '50070V', price: 50, description: '3 x 457mm straight arm', lengthMm: 457, lengthLabel: '457mm' },
    { sku: '50080RV', price: 55, description: '3 x 457mm wasted sides / curved arm', lengthMm: 457, lengthLabel: '457mm' },
    { sku: '50080R', price: 54, description: '6 x 457mm wasted sides / curved arm', lengthMm: 457, lengthLabel: '457mm' },
    { sku: '5007032', price: 51, description: '6/12 x 457mm - 32mm diameter wheel, good for curves, dressing welds', lengthMm: 457, lengthLabel: '457mm' },
    { sku: '50050', price: 52, description: '12 x 457mm - most popular arm, cork & foam platen pads', lengthMm: 457, lengthLabel: '457mm' },
    { sku: '50050BO', price: 52, description: '12 x 457mm - "boomerang" arm, best for blending', lengthMm: 457, lengthLabel: '457mm' },
    { sku: '50090', price: 62, description: '19 x 457mm - cork & foam platen pads', lengthMm: 457, lengthLabel: '457mm' },
    { sku: '50071VBF', price: 51, description: '3 x 533mm straight arm', lengthMm: 533, lengthLabel: '533mm' },
    { sku: '50081RVBF', price: 56, description: '3 x 533mm wasted sides / curved arm', lengthMm: 533, lengthLabel: '533mm' },
    { sku: '50081RBF', price: 55, description: '6 x 533mm wasted sides / curved arm', lengthMm: 533, lengthLabel: '533mm' },
    { sku: '5007132BF', price: 53, description: '6/12 x 533mm - 32mm diameter wheel, good for curves, dressing welds', lengthMm: 533, lengthLabel: '533mm' },
    { sku: '50051BF', price: 55, description: '12 x 533mm - most popular arm, cork & foam platen pads', lengthMm: 533, lengthLabel: '533mm' },
    { sku: '50051BOBF', price: 54, description: '12 x 533mm - "boomerang" arm, best for blending', lengthMm: 533, lengthLabel: '533mm' },
    { sku: '50091BF', price: 75, description: '19 x 533mm - cork & foam platen pads', lengthMm: 533, lengthLabel: '533mm' },
    { sku: '50072VBF', price: 52, description: '3 x 610mm straight arm', lengthMm: 610, lengthLabel: '610mm' },
    { sku: '50082RVBF', price: 57, description: '3 x 610mm wasted sides / curved arm', lengthMm: 610, lengthLabel: '610mm' },
    { sku: '50082RBF', price: 56, description: '6 x 610mm wasted sides / curved arm', lengthMm: 610, lengthLabel: '610mm' },
    { sku: '5007232BF', price: 54, description: '6/12 x 610mm - 32mm diameter wheel, good for curves, dressing welds', lengthMm: 610, lengthLabel: '610mm' },
    { sku: '50052BF', price: 53, description: '12 x 610mm - most popular arm, cork & foam platen pads', lengthMm: 610, lengthLabel: '610mm' },
    { sku: '50052BOBF', price: 55, description: '12 x 610mm - "boomerang" arm, best for blending', lengthMm: 610, lengthLabel: '610mm' },
    { sku: '50092BF', price: 66, description: '19 x 610mm - cork & foam platen pads', lengthMm: 610, lengthLabel: '610mm' }
  ];

  function buildBiArms(idPrefix, variantIds) {
    return BI_ARM_SPECS.map(function (spec, i) {
      return {
        id: idPrefix + '-' + spec.sku.toLowerCase(),
        sku: spec.sku,
        variantId: variantIds[i],
        price: spec.price,
        description: spec.description,
        lengthMm: spec.lengthMm,
        lengthLabel: spec.lengthLabel
      };
    });
  }

  // Real Shopify variant IDs for the three motor brands/types available at
  // each voltage on BF's two-step Electric pick (see resolveMotor()
  // below). Shared with assets/power-unit-picker.js's identical map for
  // BG20/BG30 -- keep both in sync if these products are ever repriced or
  // replaced.
  var ELECTRIC_BRAND_OPTIONS = {
    '240V': [
      { id: 'green-fixed', name: 'Green 800W Fixed Speed', variantId: '62522681491843', price: 150 },
      { id: 'bosch-variable', name: 'Bosch 900W Variable Speed', variantId: '62522681557379', price: 240 },
      { id: 'metabo-variable', name: 'Metabo 1100W Variable Speed', variantId: '62522681655683', price: 260 }
    ],
    '110V': [
      { id: 'green-fixed', name: 'Green 800W Fixed Speed', variantId: '62522681524611', price: 150 },
      { id: 'bosch-variable', name: 'Bosch 900W Variable Speed', variantId: '62522681590147', price: 240 },
      { id: 'metabo-variable', name: 'Metabo 1100W Variable Speed', variantId: '62522681688451', price: 260 }
    ]
  };

  var BELTIT_CONFIG = {
    machines: [
      {
        id: 'bf',
        name: 'Beltafile (BF)',
        tagline: 'Compact belt file for detail work in tight spaces.',
        icon: 'belt',
        housingVariantId: '62522641940867',
        housingPrice: 300,
        motors: [
          { id: 'bf-pneumatic', name: 'Pneumatic 0.5hp', badge: 'Pneumatic', variantId: '62522638008707', price: 121 },
          { id: 'bf-electric-240', name: 'Electric 240V', badge: 'Electric', voltage: '240V' },
          { id: 'bf-electric-110', name: 'Electric 110V', badge: 'Electric', voltage: '110V' }
        ],
        armsGroupLabel: 'Beltafile Arms',
        arms: [
          { id: 'bf-bf003', sku: 'BF003', variantId: '62054527336835', price: 53, description: '3 x 457mm hooked arm', lengthMm: 457, lengthLabel: '457mm' },
          { id: 'bf-bf004', sku: 'BF004', variantId: '62054527369603', price: 49, description: '4/6 x 457mm arm for slots and narrow channels', lengthMm: 457, lengthLabel: '457mm' },
          { id: 'bf-bf006', sku: 'BF006', variantId: '62054527402371', price: 49, description: '6/12 x 457mm arm for slots and narrow channels', lengthMm: 457, lengthLabel: '457mm' },
          { id: 'bf-bf012', sku: 'BF012', variantId: '62054527435139', price: 49, description: '12 x 457mm straight arm', lengthMm: 457, lengthLabel: '457mm' },
          { id: 'bf-bf019', sku: 'BF019', variantId: '62054527467907', price: 53, description: '19 x 457mm straight arm', lengthMm: 457, lengthLabel: '457mm' },
          { id: 'bf-bf003-5', sku: 'BF003/5', variantId: '62054527500675', price: 60, description: '3 x 533mm hooked arm', lengthMm: 533, lengthLabel: '533mm' },
          { id: 'bf-bf004-5', sku: 'BF004/5', variantId: '62054527533443', price: 56, description: '4/6 x 533mm arm for slots and narrow channels', lengthMm: 533, lengthLabel: '533mm' },
          { id: 'bf-bf006-5', sku: 'BF006/5', variantId: '62054527566211', price: 56, description: '6/12 x 533mm arm for slots and narrow channels', lengthMm: 533, lengthLabel: '533mm' },
          { id: 'bf-bf012-5', sku: 'BF012/5', variantId: '62054527598979', price: 56, description: '12 x 533mm straight arm', lengthMm: 533, lengthLabel: '533mm' },
          { id: 'bf-bf019-5', sku: 'BF019/5', variantId: '62054527631747', price: 63, description: '19 x 533mm straight arm', lengthMm: 533, lengthLabel: '533mm' },
          { id: 'bf-bf003-6', sku: 'BF003/6', variantId: '62054527664515', price: 61, description: '3 x 610mm hooked arm', lengthMm: 610, lengthLabel: '610mm' },
          { id: 'bf-bf004-6', sku: 'BF004/6', variantId: '62054527697283', price: 57, description: '4/6 x 610mm arm for slots and narrow channels', lengthMm: 610, lengthLabel: '610mm' },
          { id: 'bf-bf006-6', sku: 'BF006/6', variantId: '62054527730051', price: 57, description: '6/12 x 610mm arm for slots and narrow channels', lengthMm: 610, lengthLabel: '610mm' },
          { id: 'bf-bf012-6', sku: 'BF012/6', variantId: '62054527762819', price: 57, description: '12 x 610mm straight arm', lengthMm: 610, lengthLabel: '610mm' },
          { id: 'bf-bf019-6', sku: 'BF019/6', variantId: '62054527795587', price: 64, description: '19 x 610mm straight arm', lengthMm: 610, lengthLabel: '610mm' }
        ]
      },
      {
        id: 'baby',
        name: 'Baby Belt-It (Universal)',
        tagline: 'General purpose Beltit for common fabrication and finishing work.',
        icon: 'engineering',
        housingVariantId: '62522642006403',
        housingPrice: 350,
        motors: [
          { id: 'baby-pneumatic', name: 'Pneumatic 0.5hp', badge: 'Pneumatic', variantId: '62522638008707', price: 121 },
          { id: 'baby-electric-240', name: 'Electric 240V', badge: 'Electric', variantId: '62522681557379', price: 240 },
          { id: 'baby-electric-110', name: 'Electric 110V', badge: 'Electric', variantId: '62522681590147', price: 240 }
        ],
        armsGroupLabel: 'Baby BELT-IT Arms',
        arms: buildBiArms('baby', [
          '62054527828355', '62054527861123', '62054527893891', '62054527926659',
          '62054527959427', '62054527992195', '62054528024963', '62054528057731',
          '62054528090499', '62054528123267', '62054528156035', '62054528188803',
          '62054528221571', '62054528254339', '62054528287107', '62054528319875',
          '62054528352643', '62054528385411', '62054528418179', '62054528450947',
          '62054528483715'
        ])
      },
      {
        id: 'advanced',
        name: 'Advanced Belt-It',
        tagline: 'The all-rounder, built for mixed production work.',
        icon: 'beltarm',
        housingVariantId: '62522642039171',
        housingPrice: 400,
        motors: [
          { id: 'advanced-pneumatic', name: 'Pneumatic 0.8hp', badge: 'Pneumatic', variantId: '62522638041475', price: 185 },
          { id: 'advanced-electric-240', name: 'Electric 240V', badge: 'Electric', variantId: '62522681655683', price: 260 },
          { id: 'advanced-electric-110', name: 'Electric 110V', badge: 'Electric', variantId: '62522681688451', price: 260 }
        ],
        armsGroupLabel: 'Advanced BELT-IT Arms',
        arms: buildBiArms('adv', [
          '62054528778627', '62054528811395', '62054528844163', '62054528876931',
          '62054528909699', '62054528942467', '62054528975235', '62054529008003',
          '62054529040771', '62054529073539', '62054529106307', '62054529139075',
          '62054529171843', '62054529204611', '62054529237379', '62054529270147',
          '62054529302915', '62054529335683', '62054529368451', '62054529401219',
          '62054529433987'
        ])
      },
      {
        id: 'heavyduty',
        name: 'Heavy Duty Belt-It',
        tagline: 'Maximum power and reach for high-volume, heavy-stock removal.',
        icon: 'roller',
        housingVariantId: '62522642071939',
        housingPrice: 450,
        motors: [
          { id: 'hd-pneumatic', name: 'Pneumatic 0.8hp', badge: 'Pneumatic', variantId: '62522638041475', price: 185 },
          { id: 'hd-electric-240', name: 'Electric 240V', badge: 'Electric', variantId: '62522681655683', price: 260 },
          { id: 'hd-electric-110', name: 'Electric 110V', badge: 'Electric', variantId: '62522681688451', price: 260 }
        ],
        armsGroupLabel: 'Heavy Duty BELT-IT Arms',
        arms: buildBiArms('hd', [
          '62054529991043', '62054530023811', '62054530056579', '62054530089347',
          '62054530122115', '62054530154883', '62054530187651', '62054530220419',
          '62054530253187', '62054530285955', '62054530318723', '62054530351491',
          '62054530384259', '62054530417027', '62054530449795', '62054530482563',
          '62054530515331', '62054530548099', '62054530580867', '62054530613635',
          '62054530646403'
        ])
      }
    ]
  };

  function getMachine(id) {
    return BELTIT_CONFIG.machines.filter(function (m) { return m.id === id; })[0] || null;
  }

  function getMotor(machine, id) {
    if (!machine) return null;
    return machine.motors.filter(function (m) { return m.id === id; })[0] || null;
  }

  function getBrandOptions(motorOption) {
    return motorOption && motorOption.voltage ? (ELECTRIC_BRAND_OPTIONS[motorOption.voltage] || []) : [];
  }

  function getBrandOption(motorOption, brandId) {
    return getBrandOptions(motorOption).filter(function (b) { return b.id === brandId; })[0] || null;
  }

  // Resolves a motor selection down to the real, addable product data.
  // Flat motors (Pneumatic, and Baby/Advanced/Heavy Duty's single-brand
  // Electric options) already carry their own variantId/price and resolve
  // directly. BF's Electric 240V/110V options are two-step: they only name
  // a voltage, so brandId must additionally identify one of the three
  // shared Motor Unit products at that voltage (Green 800W Fixed / Bosch
  // 900W Variable / Metabo 1100W Variable) -- returns null until both a
  // voltage and a brand have been chosen.
  function resolveMotor(machine, motorId, brandId) {
    var option = getMotor(machine, motorId);
    if (!option) return null;
    if (!option.voltage) {
      return { name: option.name, badge: option.badge, variantId: option.variantId, price: option.price };
    }
    var brand = getBrandOption(option, brandId);
    if (!brand) return null;
    return { name: option.name + ' — ' + brand.name, badge: option.badge, variantId: brand.variantId, price: brand.price };
  }

  function getArmOption(machine, armId) {
    if (!machine) return null;
    return machine.arms.filter(function (a) { return a.id === armId; })[0] || null;
  }

  // Groups a machine's arms by belt length (the larger of the two numbers
  // in an arm's spec, e.g. "457mm" or "610mm"), sorted shortest-to-longest.
  // Returns null if none of the arms carry a lengthMm value, so callers can
  // fall back to a flat list -- kept as a safety net, though every housing
  // now has length data.
  function groupArmsByLength(arms) {
    if (!arms.some(function (a) { return a.lengthMm != null; })) return null;

    var groups = [];
    arms.forEach(function (arm) {
      var label = arm.lengthLabel || 'Other';
      var group = groups.filter(function (g) { return g.label === label; })[0];
      if (!group) {
        group = { label: label, lengthMm: arm.lengthMm != null ? arm.lengthMm : Infinity, arms: [] };
        groups.push(group);
      }
      group.arms.push(arm);
    });
    groups.sort(function (a, b) { return a.lengthMm - b.lengthMm; });
    return groups;
  }

  function formatPrice(amount) {
    return amount > 0 ? '£' + amount.toFixed(2) : 'POA';
  }

  // Motor prices (Pneumatic, flat Electric, and brand sub-options) are all
  // an add-on cost on top of the housing -- shown "+£121.00" style so the
  // motor step's cards read consistently, whether the option resolves
  // directly (Pneumatic) or via a voltage->brand pick (BF Electric).
  function formatAddOnPrice(amount) {
    return amount > 0 ? '+' + formatPrice(amount) : 'POA';
  }

  // ---------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------

  var state = {
    step: 1,
    machineId: null,
    motorId: null,
    motorBrandId: null, // only set when the confirmed motor is a voltage-group option (BF Electric)
    arms: [], // { key, sku, description, variantId, price, quantity }
    // "Preview" selections drive the preview panel on each step, and only
    // become the real (confirmed) selections above once the shopper clicks
    // Confirm Selection (housing/motor) or Add to Cart (arms). Kept in
    // sync with the confirmed value once confirmed, so navigating back to
    // an earlier step via "Change housing"/"Change motor" shows the
    // previously-confirmed choice rather than resetting to blank.
    previewMachineId: null,
    previewMotorId: null,
    previewMotorBrandId: null,
    previewArmId: null
  };

  // Guards against a double add-to-cart when the shopper has two "Add to
  // Cart" triggers on screen at once (the sidebar one plus the one under
  // the arms grid on step 3) and manages to click both before the first
  // request resolves.
  var isAddingToCart = false;

  function resetAfterMachineChange() {
    state.motorId = null;
    state.motorBrandId = null;
    state.arms = [];
    state.previewMotorId = null;
    state.previewMotorBrandId = null;
    state.previewArmId = null;
  }

  function armIsSelected(key) {
    return state.arms.some(function (a) { return a.key === key; });
  }

  function toggleArm(armOption) {
    var idx = -1;
    for (var i = 0; i < state.arms.length; i++) {
      if (state.arms[i].key === armOption.id) { idx = i; break; }
    }
    if (idx > -1) {
      state.arms.splice(idx, 1);
    } else {
      state.arms.push({
        key: armOption.id,
        sku: armOption.sku,
        description: armOption.description || '',
        variantId: armOption.variantId,
        price: armOption.price,
        quantity: 1
      });
    }
  }

  function removeArmLine(key) {
    state.arms = state.arms.filter(function (a) { return a.key !== key; });
  }

  function setArmQuantity(key, quantity) {
    var line = state.arms.filter(function (a) { return a.key === key; })[0];
    if (line) line.quantity = Math.max(1, quantity);
  }

  // A housing is never sold on its own in this flow -- the wizard always
  // pairs it with a motor choice before it counts as addable, so "having a
  // machine selected" only becomes an addable line once a motor is chosen
  // too. Arms are standalone real products, so they count on their own.
  function hasAnySelection() {
    return !!state.motorId || state.arms.length > 0;
  }

  // ---------------------------------------------------------------------
  // Cart integration
  // ---------------------------------------------------------------------

  function buildLineItems() {
    var machine = getMachine(state.machineId);
    var motor = resolveMotor(machine, state.motorId, state.motorBrandId);
    var items = [];
    if (machine && motor) {
      items.push({
        variantId: machine.housingVariantId,
        name: machine.name + ' Housing',
        quantity: 1,
        price: machine.housingPrice,
        kind: 'Housing'
      });
      items.push({
        variantId: motor.variantId,
        name: motor.name,
        quantity: 1,
        price: motor.price,
        kind: 'Motor'
      });
    }
    state.arms.forEach(function (arm) {
      items.push({
        variantId: arm.variantId,
        name: 'Arm ' + arm.sku,
        quantity: arm.quantity,
        price: arm.price,
        kind: 'Arm'
      });
    });
    return items;
  }

  // Refreshes the header cart icon bubble (and the cart drawer's own
  // markup, if the theme is in drawer mode) after adding a configuration,
  // WITHOUT opening the drawer -- see file header for why. Reuses the real
  // <cart-drawer> element's own getSectionsToRender()/getSectionInnerHTML()
  // methods so this stays in sync with however assets/cart-drawer.js
  // patches sections elsewhere in the theme, rather than re-implementing
  // that logic separately here.
  function updateCartUI(data) {
    if (!data || !data.sections) return;
    var cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      cartDrawer.getSectionsToRender().forEach(function (section) {
        var html = data.sections[section.id];
        if (!html) return;
        var sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement) return;
        sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(html, section.selector);
      });
      var inner = cartDrawer.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      return;
    }

    // No drawer on this theme/setting -- just patch the header bubble directly.
    var bubbleEl = document.getElementById('cart-icon-bubble');
    var bubbleHtml = data.sections['cart-icon-bubble'];
    if (bubbleEl && bubbleHtml) {
      var wrapper = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
      if (wrapper) bubbleEl.innerHTML = wrapper.innerHTML;
    }
  }

  function addConfigurationToCart(items) {
    var cartDrawer = document.querySelector('cart-drawer');
    var sections =
      cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(function (s) { return s.id; })
        : ['cart-icon-bubble'];

    var payload = {
      items: items.map(function (i) {
        return {
          id: i.variantId,
          quantity: i.quantity,
          properties: { _BeltitConfiguration: 'true' }
        };
      }),
      sections: sections,
      sections_url: window.location.pathname
    };
    return fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          // Shopify's /cart/add.js can return a normal, parseable JSON body
          // even on FAILURE -- e.g. a sold-out variant, or a storefront-level
          // rate-limit/bot check intercepting the request. A fetch that
          // merely didn't throw is not the same as one that actually added
          // something to the cart, so treat a non-2xx response, or a body
          // carrying Shopify's error shape (`status` present), as a failure
          // instead of silently showing the "Added to your basket" screen.
          if (!res.ok || (data && data.status)) {
            var message = (data && (data.description || data.message)) ||
              'Unable to add this configuration to your basket. Please try again in a moment.';
            var error = new Error(message);
            error.isCartError = true;
            throw error;
          }
          return data;
        });
      })
      .then(function (data) {
        updateCartUI(data);
        return { simulated: false, data: data, items: items };
      });
  }

  // ---------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------

  var root = null;
  var icons = {};
  var productImages = {};

  var STEP_LABELS = ['Housing', 'Motor', 'Arms', 'Confirmation'];

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  // Reflects what's actually been selected rather than just "which step
  // number are we past", since the sidebar's Add to Cart button can take
  // the shopper straight to the confirmation page from any earlier step.
  function renderProgress() {
    var machine = getMachine(state.machineId);
    var motor = resolveMotor(machine, state.motorId, state.motorBrandId);

    var stepComplete = [!!machine, !!motor, state.arms.length > 0, false];
    var stepReachable = [true, !!machine, !!machine, false];

    var html = '<ol class="beltit-progress">';
    STEP_LABELS.forEach(function (label, i) {
      var n = i + 1;
      var isCurrent = n === state.step;
      var isDone = !isCurrent && stepComplete[i];
      var status = isCurrent ? 'is-current' : isDone ? 'is-done' : 'is-upcoming';
      var clickable = !isCurrent && stepReachable[i];
      html += '<li class="beltit-progress__item ' + status + '"' +
        (clickable ? ' data-goto-step="' + n + '" tabindex="0" role="button"' : '') + '>' +
        '<span class="beltit-progress__index">' + (isDone ? '✓' : n) + '</span>' +
        '<span class="beltit-progress__label">' + label + '</span>' +
        '</li>';
    });
    html += '</ol>';
    return html;
  }

  function computeTotals() {
    var machine = getMachine(state.machineId);
    var motor = resolveMotor(machine, state.motorId, state.motorBrandId);
    var total = 0;
    var hasPoa = false;

    if (machine && motor) {
      total += machine.housingPrice;
      total += motor.price;
    }
    state.arms.forEach(function (arm) {
      if (arm.price > 0) total += arm.price * arm.quantity; else hasPoa = true;
    });

    return { total: total, hasPoa: hasPoa };
  }

  function renderSummary() {
    var machine = getMachine(state.machineId);
    var motor = resolveMotor(machine, state.motorId, state.motorBrandId);
    var hasSelection = hasAnySelection();

    var rows = '';
    if (machine) {
      rows += '<li><span>Housing</span><strong>' + escapeHtml(machine.name) + '</strong></li>';
    }
    if (motor) {
      rows += '<li><span>Motor</span><strong>' + escapeHtml(motor.name) + '</strong></li>';
    }
    state.arms.forEach(function (arm) {
      rows += '<li><span>Arm' + (arm.quantity > 1 ? ' × ' + arm.quantity : '') + '</span><strong>' + escapeHtml(arm.sku) + '</strong></li>';
    });

    var totals = computeTotals();
    var totalLine = hasSelection
      ? '<p class="beltit-summary__total">Total: ' + formatPrice(totals.total) + (totals.hasPoa ? ' + POA item(s)' : '') + '</p>'
      : '';

    var body = hasSelection
      ? '<ul class="beltit-summary__list">' + rows + '</ul>' + totalLine
      : (machine
          ? '<p class="beltit-summary__empty">Housing chosen. Pick a motor to continue.</p>'
          : '<p class="beltit-summary__empty">Nothing selected yet.</p>');

    // Lets the shopper add whatever they've picked so far straight to the
    // basket from wherever they are in the flow. Hidden once already on
    // the confirmation page, where it'd be redundant.
    var addToCartButton = state.step < 4
      ? '<button type="button" class="button beltit-summary__review" data-add-to-basket' + (hasSelection ? '' : ' disabled') + '>Add to Cart</button>'
      : '';

    return (
      '<aside class="beltit-summary">' +
      '<h2 class="beltit-summary__title">Your configuration</h2>' +
      body +
      addToCartButton +
      '</aside>'
    );
  }

  // Shared preview-panel markup: a product photo (falling back to a plain
  // placeholder when no image is available yet) alongside name/subtitle/
  // description/price and a confirm-style action button. Used for the
  // Housing, Motor and Arms steps alike -- only the button markup passed
  // in differs (Confirm Selection vs. Add to Cart).
  function renderPreviewPanel(item, image, buttonHtml) {
    if (!item) return '';
    return (
      '<div class="beltit-preview">' +
      '<div class="beltit-preview__media">' +
      (image
        ? '<img src="' + image + '" alt="" class="beltit-preview__image" loading="lazy">'
        : '<div class="beltit-preview__placeholder">Photo coming soon</div>') +
      '</div>' +
      '<div class="beltit-preview__details">' +
      '<h3 class="beltit-preview__title">' + escapeHtml(item.title) + '</h3>' +
      (item.subtitle ? '<p class="beltit-preview__subtitle">' + escapeHtml(item.subtitle) + '</p>' : '') +
      (item.description ? '<p class="beltit-preview__desc">' + escapeHtml(item.description) + '</p>' : '') +
      (item.price != null ? '<p class="beltit-preview__price">' + formatPrice(item.price) + '</p>' : '') +
      buttonHtml +
      '</div>' +
      '</div>'
    );
  }

  function renderStep1() {
    var cards = BELTIT_CONFIG.machines.map(function (machine) {
      var selected = state.previewMachineId === machine.id;
      return (
        '<button type="button" class="beltit-card' + (selected ? ' is-selected' : '') + '" data-select-machine="' + machine.id + '">' +
        (selected ? '<span class="beltit-card__check">✓</span>' : '') +
        '<span class="beltit-card__icon">' + (icons[machine.icon] || '') + '</span>' +
        '<span class="beltit-card__title">' + escapeHtml(machine.name) + '</span>' +
        '<span class="beltit-card__desc">' + escapeHtml(machine.tagline) + '</span>' +
        '</button>'
      );
    }).join('');

    var previewMachine = getMachine(state.previewMachineId);
    var previewImage = previewMachine ? productImages[previewMachine.housingVariantId] : null;
    var confirmButton = '<button type="button" class="button" data-confirm-housing>Confirm Selection</button>';
    var preview = previewMachine
      ? renderPreviewPanel({
          title: previewMachine.name,
          subtitle: null,
          description: previewMachine.tagline,
          price: previewMachine.housingPrice
        }, previewImage, confirmButton)
      : '';

    return (
      '<div class="beltit-step">' +
      '<h1 class="beltit-step__title">Choose your machine housing</h1>' +
      '<p class="beltit-step__intro">Pick the Belt-It housing that fits your work. This decides which motors and arms are available next. Click a card to preview it, then Confirm Selection to continue.</p>' +
      preview +
      '<div class="beltit-card-grid beltit-card-grid--3">' + cards + '</div>' +
      '</div>'
    );
  }

  function renderStep2() {
    var machine = getMachine(state.machineId);
    if (!machine) return renderStep1();

    var cards = machine.motors.map(function (motor) {
      var selected = state.previewMotorId === motor.id;
      var priceLabel = motor.voltage
        ? 'From ' + formatAddOnPrice(getBrandOptions(motor)[0].price)
        : formatAddOnPrice(motor.price);
      return (
        '<button type="button" class="beltit-card' + (selected ? ' is-selected' : '') + '" data-select-motor="' + motor.id + '">' +
        (selected ? '<span class="beltit-card__check">✓</span>' : '') +
        '<span class="beltit-card__badge">' + escapeHtml(motor.badge) + '</span>' +
        '<span class="beltit-card__title">' + escapeHtml(motor.name) + '</span>' +
        '<span class="beltit-card__desc">' + priceLabel + '</span>' +
        '</button>'
      );
    }).join('');

    var previewOption = getMotor(machine, state.previewMotorId);
    var brandCards = '';
    if (previewOption && previewOption.voltage) {
      var brands = getBrandOptions(previewOption);
      brandCards =
        '<div class="beltit-brand-intro">Choose your motor for ' + escapeHtml(previewOption.name) + ':</div>' +
        '<div class="beltit-card-grid beltit-card-grid--3 beltit-card-grid--brands">' +
        brands.map(function (brand) {
          var selected = state.previewMotorBrandId === brand.id;
          return (
            '<button type="button" class="beltit-card beltit-card--brand' + (selected ? ' is-selected' : '') + '" data-select-motor-brand="' + brand.id + '">' +
            (selected ? '<span class="beltit-card__check">✓</span>' : '') +
            '<span class="beltit-card__title">' + escapeHtml(brand.name) + '</span>' +
            '<span class="beltit-card__desc">' + formatAddOnPrice(brand.price) + '</span>' +
            '</button>'
          );
        }).join('') +
        '</div>';
    }

    var resolvedPreview = previewOption ? resolveMotor(machine, state.previewMotorId, state.previewMotorBrandId) : null;
    var previewImage = resolvedPreview ? productImages[resolvedPreview.variantId] : null;
    var confirmButton = '<button type="button" class="button" data-confirm-motor' + (resolvedPreview ? '' : ' disabled') + '>Confirm Selection</button>';
    var preview = resolvedPreview
      ? renderPreviewPanel({
          title: resolvedPreview.name,
          subtitle: resolvedPreview.badge,
          description: null,
          price: resolvedPreview.price
        }, previewImage, confirmButton)
      : '';

    return (
      '<div class="beltit-step">' +
      '<button type="button" class="beltit-back" data-goto-step="1">&larr; Change housing</button>' +
      '<h1 class="beltit-step__title">Choose a motor for the ' + escapeHtml(machine.name) + '</h1>' +
      '<p class="beltit-step__intro">Only motors compatible with the ' + escapeHtml(machine.name) + ' are shown. Click a card to preview it, then Confirm Selection to continue -- housing and motor are added to your basket as two separate items.</p>' +
      preview +
      '<div class="beltit-card-grid beltit-card-grid--3">' + cards + '</div>' +
      brandCards +
      '</div>'
    );
  }

  function renderStep3() {
    var machine = getMachine(state.machineId);
    if (!machine) return renderStep1();

    var lengthGroups = groupArmsByLength(machine.arms);
    var body;
    if (lengthGroups) {
      body = lengthGroups.map(function (group) {
        var options = group.arms.map(function (arm) {
          return renderArmCard(arm, armIsSelected(arm.id));
        }).join('');
        return (
          '<div class="beltit-arm-group">' +
          '<h3 class="beltit-arm-group__title">' + escapeHtml(group.label) + '</h3>' +
          '<div class="beltit-card-grid beltit-card-grid--3">' + options + '</div>' +
          '</div>'
        );
      }).join('');
    } else {
      body = '<div class="beltit-card-grid beltit-card-grid--3">' + machine.arms.map(function (arm) {
        return renderArmCard(arm, armIsSelected(arm.id));
      }).join('') + '</div>';
    }

    var previewArm = state.previewArmId ? getArmOption(machine, state.previewArmId) : null;
    var actionsHtml;
    if (previewArm) {
      var previewImage = productImages[previewArm.variantId];
      var addButton = '<button type="button" class="button" data-add-to-basket' + (hasAnySelection() ? '' : ' disabled') + '>Add to Cart</button>';
      actionsHtml = renderPreviewPanel({
        title: previewArm.sku,
        subtitle: previewArm.lengthLabel,
        description: previewArm.description,
        price: previewArm.price
      }, previewImage, addButton);
    } else {
      actionsHtml =
        '<div class="beltit-step__actions">' +
        '<button type="button" class="button" data-add-to-basket' + (hasAnySelection() ? '' : ' disabled') + '>Add to Cart</button>' +
        '</div>';
    }

    return (
      '<div class="beltit-step">' +
      '<button type="button" class="beltit-back" data-goto-step="2">&larr; Change motor</button>' +
      '<h1 class="beltit-step__title">Choose your arm(s)</h1>' +
      '<p class="beltit-step__intro">Arms from ' + escapeHtml(machine.armsGroupLabel) + '. Select as many as you\'d like -- click one to preview its details here, and compare as many as you like before adding to cart.</p>' +
      actionsHtml +
      body +
      '</div>'
    );
  }

  function renderArmCard(arm, selected) {
    return (
      '<button type="button" class="beltit-card beltit-card--arm' + (selected ? ' is-selected' : '') + '" data-toggle-arm="' + arm.id + '">' +
      '<span class="beltit-card__check">' + (selected ? '✓' : '') + '</span>' +
      '<span class="beltit-card__title">' + escapeHtml(arm.sku) + '</span>' +
      (arm.description ? '<span class="beltit-card__spec">' + escapeHtml(arm.description) + '</span>' : '') +
      '<span class="beltit-card__desc">' + formatPrice(arm.price) + '</span>' +
      '</button>'
    );
  }

  function renderConfirmation(result) {
    var lines = result.items.map(function (item) {
      return (
        '<li class="beltit-confirmation__line">' +
        '<div><strong>' + escapeHtml(item.name) + '</strong><span> × ' + item.quantity + '</span></div>' +
        '<code>' + formatPrice(item.price) + '</code>' +
        '</li>'
      );
    }).join('');

    return (
      '<div class="beltit-step beltit-step--confirmation">' +
      '<div class="beltit-confirmation__icon">✓</div>' +
      '<h1 class="beltit-step__title">Added to your basket</h1>' +
      '<p class="beltit-step__intro">These items have been added to your cart. POA items will need a quote before checkout. Use Request a Quote from your cart to finish those off.</p>' +
      '<ul class="beltit-confirmation__list">' + lines + '</ul>' +
      '<div class="beltit-step__actions">' +
      '<a href="/cart" class="button">View cart</a>' +
      '<button type="button" class="button button--secondary" data-start-over>Build another configuration</button>' +
      '</div>' +
      '</div>'
    );
  }

  var lastResult = null;

  function render() {
    var stepHtml;
    if (state.step === 1) stepHtml = renderStep1();
    else if (state.step === 2) stepHtml = renderStep2();
    else if (state.step === 3) stepHtml = renderStep3();
    else stepHtml = renderConfirmation(lastResult || { items: [] });

    var showSummary = state.step >= 1 && state.step <= 3;
    var showProgress = state.step <= 3;

    root.innerHTML =
      '<div class="beltit-configurator">' +
      (showProgress ? renderProgress() : '') +
      '<div class="beltit-layout' + (showSummary ? '' : ' beltit-layout--single') + '">' +
      '<div class="beltit-main">' + stepHtml + '</div>' +
      (showSummary ? renderSummary() : '') +
      '</div>' +
      '</div>';
  }

  // Only step transitions should scroll the page -- not preview clicks,
  // quantity changes, arm toggles, or line removals (those happen in
  // place, on the current step).
  function scrollToTop() {
    window.scrollTo({ top: root.getBoundingClientRect().top + window.pageYOffset - 24, behavior: 'smooth' });
  }

  // ---------------------------------------------------------------------
  // Events (delegated -- container is rebuilt on every render)
  // ---------------------------------------------------------------------

  function onClick(event) {
    var el;

    // Clicking a housing/motor card only sets the preview -- it no longer
    // jumps to the next step. That happens once Confirm Selection is
    // clicked, below.
    el = event.target.closest('[data-select-machine]');
    if (el) {
      state.previewMachineId = el.getAttribute('data-select-machine');
      render();
      return;
    }

    el = event.target.closest('[data-select-motor]');
    if (el) {
      state.previewMotorId = el.getAttribute('data-select-motor');
      state.previewMotorBrandId = null;
      render();
      return;
    }

    el = event.target.closest('[data-select-motor-brand]');
    if (el) {
      state.previewMotorBrandId = el.getAttribute('data-select-motor-brand');
      render();
      return;
    }

    el = event.target.closest('[data-confirm-housing]');
    if (el) {
      if (!state.previewMachineId) return;
      if (state.previewMachineId !== state.machineId) {
        state.machineId = state.previewMachineId;
        resetAfterMachineChange();
        state.previewMachineId = state.machineId;
      }
      state.step = 2;
      render();
      scrollToTop();
      return;
    }

    el = event.target.closest('[data-confirm-motor]');
    if (el) {
      if (el.disabled) return;
      if (!state.previewMotorId) return;
      var confirmMachine = getMachine(state.machineId);
      var confirmOption = getMotor(confirmMachine, state.previewMotorId);
      if (confirmOption && confirmOption.voltage && !state.previewMotorBrandId) return;
      state.motorId = state.previewMotorId;
      state.motorBrandId = confirmOption && confirmOption.voltage ? state.previewMotorBrandId : null;
      state.step = 3;
      render();
      scrollToTop();
      return;
    }

    el = event.target.closest('[data-toggle-arm]');
    if (el) {
      var machine = getMachine(state.machineId);
      var armId = el.getAttribute('data-toggle-arm');
      var armOption = getArmOption(machine, armId);
      if (armOption) toggleArm(armOption);
      state.previewArmId = armId;
      render();
      return;
    }

    el = event.target.closest('[data-goto-step]');
    if (el) {
      if (el.disabled) return;
      state.step = parseInt(el.getAttribute('data-goto-step'), 10);
      render();
      scrollToTop();
      return;
    }

    el = event.target.closest('[data-remove-arm]');
    if (el) {
      removeArmLine(el.getAttribute('data-remove-arm'));
      render();
      return;
    }

    el = event.target.closest('[data-qty-increase]');
    if (el) {
      var incKey = el.getAttribute('data-qty-increase');
      var incLine = state.arms.filter(function (a) { return a.key === incKey; })[0];
      if (incLine) setArmQuantity(incKey, incLine.quantity + 1);
      render();
      return;
    }

    el = event.target.closest('[data-qty-decrease]');
    if (el) {
      var decKey = el.getAttribute('data-qty-decrease');
      var decLine = state.arms.filter(function (a) { return a.key === decKey; })[0];
      if (decLine) setArmQuantity(decKey, decLine.quantity - 1);
      render();
      return;
    }

    el = event.target.closest('[data-add-to-basket]');
    if (el) {
      if (el.disabled || isAddingToCart) return;
      isAddingToCart = true;
      el.disabled = true;
      el.textContent = 'Adding…';
      var items = buildLineItems();
      addConfigurationToCart(items)
        .then(function (result) {
          isAddingToCart = false;
          lastResult = result;
          state.step = 4;
          render();
          scrollToTop();
        })
        .catch(function (error) {
          isAddingToCart = false;
          el.disabled = false;
          el.textContent = 'Add to Cart';
          var message = (error && error.isCartError && error.message) ||
            'Something went wrong adding this configuration to your basket. Please try again.';
          window.alert(message);
        });
      return;
    }

    el = event.target.closest('[data-start-over]');
    if (el) {
      state.step = 1;
      state.machineId = null;
      state.motorId = null;
      state.motorBrandId = null;
      state.arms = [];
      state.previewMachineId = null;
      state.previewMotorId = null;
      state.previewMotorBrandId = null;
      state.previewArmId = null;
      lastResult = null;
      render();
      scrollToTop();
      return;
    }
  }

  // ---------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------

  document.addEventListener('DOMContentLoaded', function () {
    root = document.getElementById('BeltitConfigurator');
    if (!root) return;

    try {
      icons = JSON.parse(root.getAttribute('data-icons') || '{}');
    } catch (e) {
      icons = {};
    }

    try {
      productImages = JSON.parse(root.getAttribute('data-product-images') || '{}');
    } catch (e) {
      productImages = {};
    }

    root.addEventListener('click', onClick);
    render();
  });
})();
